import * as React from 'react';
import { ModalHeader, Modal, ModalBody, Carousel, CarouselItem, FormGroup, Label, Button, Input } from 'reactstrap';
import { TranslationFunction } from 'i18next';
import { has } from 'lodash';
import Radio from '../../shared/Radio';
import Checkbox from '../../shared/Checkbox';
import AgreeToTermsAndConditions from '../../shared/AgreeToTermsAndConditions';
import { SignupState } from './Signup';
import { CompetitionConfig } from '../../userApi';

interface Props {
  t: TranslationFunction;
  canSetHealthSystemsEmployee: boolean;
  update: (update: Partial<SignupState>) => void;
  submit: () => void;
  signupState: SignupState;
  competitionConfig: CompetitionConfig;
}

export default class SignupWizard extends React.Component<Props> {

  private previous(maxStep: number) {
    const { update, signupState: { signupWizardStep } } = this.props;
    if (signupWizardStep > 0) {
      update({ signupWizardStep: signupWizardStep - 1 });
    }
  }

  private next(maxStep: number) {
    const { update, signupState: { signupWizardStep } } = this.props;
    if (signupWizardStep < maxStep) {
      update({ signupWizardStep: signupWizardStep + 1 });
    }
  }

  public render() {
    const { t, signupState, update, submit, canSetHealthSystemsEmployee, competitionConfig } = this.props;
    const { form, agreedToTermsAndConditions, signupWizardStep, signupTeam } = signupState;

    const items: React.ReactNode[] = [];
    const hasCustomTeams = Boolean(
      competitionConfig.customSignupTeamLabels &&
      competitionConfig.customSignupTeamLabels.length &&
      competitionConfig.customSignupTeamIds &&
      competitionConfig.customSignupTeamIds.length
    );

    if (hasCustomTeams) {
      items.push('team');
    }
    if (canSetHealthSystemsEmployee) {
      items.push('health');
    }
    items.push('terms');

    const maxStep = items.length - 1;
    const teamLabel = (competitionConfig && competitionConfig.teamLabel) || t('Team');
    const useSelect = hasCustomTeams && competitionConfig.customSignupTeamLabels.length > 8;

    const carouselItems = items.map((step, stepIndex) => {
      if (step === 'team') {
        return (
          <CarouselItem key="team">
            <FormGroup
              id="signup_form__user_team"
              role={useSelect ? undefined : 'radiogroup'}
              aria-labelledby={'signup_form__team'}
            >
              <Label id={'signup_form__team'}>{`Choose your ${teamLabel}`}</Label>
              {competitionConfig.templateSuffix === 'ccsf' && (
                <p className="text-muted small">
                  {t('Staff outside the listed agencies can select Other. You can still join the Challenge; pilot awards and prizes apply to participating agencies only.')}
                </p>
              )}
              <br />
              {useSelect ? (
                <Input
                  type="select"
                  value={signupTeam || ''}
                  onChange={(e) => update({ signupTeam: e.target.value })}
                  aria-labelledby="signup_form__team"
                >
                  <option value="">{t('Select…')}</option>
                  {competitionConfig.customSignupTeamLabels.map((label, index) => (
                    <option key={competitionConfig.customSignupTeamIds[index]} value={competitionConfig.customSignupTeamIds[index]}>
                      {label}
                    </option>
                  ))}
                </Input>
              ) : (
                competitionConfig.customSignupTeamLabels.map((label, index) => (
                  <React.Fragment key={competitionConfig.customSignupTeamIds[index]}>
                    <Radio
                      name={'user_team'}
                      value={competitionConfig.customSignupTeamIds[index]}
                      checked={signupTeam === competitionConfig.customSignupTeamIds[index]}
                      onChange={(userTeam) => update({ signupTeam: userTeam })}
                    >{label}</Radio>&nbsp;&nbsp;
                  </React.Fragment>
                ))
              )}
            </FormGroup>
            <div className="cc__signup_wizard__controls">
              <Button
                outline={true}
                size="sm"
                color="primary"
                disabled={!signupTeam}
                onClick={() => this.next(maxStep)}
                className="cc__signup_wizard__controls__next"
              >{t('Next')}</Button>
            </div>
          </CarouselItem>
        );
      }

      if (step === 'health') {
        return (
          <CarouselItem key="health">
            <FormGroup
              id="signup_form__healthFacilityEmployee"
              role={'radiogroup'}
              aria-labelledby={'signup_form__health_facility_employee__label'}
            >
              <Label id="signup_form__health_facility_employee__label">{t('Are you a UC Health System employee?')}</Label><br />
              <Radio
                name={'signup_form__health_facility_employee'}
                value={'1'}
                checked={form.options.isHealthFacilityEmployee === true}
                onChange={(value) => update({ form: { ...form, options: { ...form.options, isHealthFacilityEmployee: value === '1' } } })}
              >{t('Yes')}</Radio>&nbsp;&nbsp;
              <Radio
                name={'signup_form__health_facility_employee'}
                value={'0'}
                checked={form.options.isHealthFacilityEmployee === false}
                onChange={(value) => update({ form: { ...form, options: { ...form.options, isHealthFacilityEmployee: value === '1' } } })}
              >{t('No')}</Radio>&nbsp;&nbsp;
            </FormGroup>
            <div className="cc__signup_wizard__controls">
              {stepIndex > 0 &&
                <Button
                  outline={true}
                  size="sm"
                  color="primary"
                  onClick={() => this.previous(maxStep)}
                  className="cc__signup_wizard__controls__prev"
                >{t('Back')}</Button>
              }
              <Button
                outline={true}
                size="sm"
                color="primary"
                disabled={!has(form.options, 'isHealthFacilityEmployee')}
                onClick={() => this.next(maxStep)}
                className="cc__signup_wizard__controls__next"
              >{t('Next')}</Button>
            </div>
          </CarouselItem>
        );
      }

      return (
        <CarouselItem key="terms">
          <FormGroup
            id="signup_form__agreedToTermsAndConditions"
          >
            <Checkbox
              checked={Boolean(agreedToTermsAndConditions)}
              onChange={(checked) => checked ?
                update({ agreedToTermsAndConditions: new Date().toISOString() }) :
                update({ agreedToTermsAndConditions: '' })
              }
            >
              <AgreeToTermsAndConditions />
            </Checkbox>
          </FormGroup>
          <div className="cc__signup_wizard__controls">
            {stepIndex > 0 &&
              <Button
                outline={true}
                size="sm"
                color="primary"
                onClick={() => this.previous(maxStep)}
                className="cc__signup_wizard__controls__prev"
              >{t('Back')}</Button>
            }
            <Button
              color="primary"
              size="sm"
              disabled={!Boolean(agreedToTermsAndConditions)}
              onClick={() => submit()}
              id="cc__signup_wizard__controls__submit"
            >{t('Submit')}</Button>
          </div>
        </CarouselItem>
      );
    });

    return (
      <Modal
        isOpen={signupState.isWizardOpen}
        id="signup_form_wizard"
        onClosed={() => update({ isWizardOpen: false })}
        toggle={() => update({ isWizardOpen: !signupState.isWizardOpen })}
      >
        <ModalHeader
          toggle={() => update({ isWizardOpen: !signupState.isWizardOpen })}
        >{t('Complete your registration')}</ModalHeader>
        <ModalBody>
          <Carousel
            interval={false}
            next={() => this.next(maxStep)}
            previous={() => this.previous(maxStep)}
            activeIndex={signupWizardStep}
          >
            {carouselItems}
          </Carousel>
        </ModalBody>
      </Modal>
    );
  }
}
